import { Ban, LineChart } from "lucide-react";

import type { SigningStats } from "../api";
import { formatCount, formatShare, signingRollout } from "./signingRollout";
import styles from "./RequestSigningStats.module.css";

/**
 * The rollout counters, above the section's fields rather than inside a
 * FieldRow: they are read-only, and the row's left column would promise a
 * config key they do not have. They stay in this card all the same, because
 * the enforce decision is made by them and sending an operator to another page
 * for it means getting the decision made blind (§8.3).
 */
interface Props {
  /** The answer, or undefined while it is in flight or after it failed. */
  stats: SigningStats | undefined;
  /** The request itself failed — a different silence from `available: false`. */
  failed: boolean;
  /** The mode in FORCE — what the empty state talks about, since nothing is
   *  counted until a mode that counts has actually been saved. */
  mode: string;
  /** The window in force, named in the `timestamp` gloss. */
  clockSkewSec: number;
}

/** What each result means, in the words of someone who has not read §3. */
const GLOSS: Record<string, string> = {
  missing: "request carried no signature — build not updated yet",
  invalid: "signature did not match the request",
  timestamp: "client clock outside the ±N s window",
  replay: "nonce already seen",
  unsupported_format: "signature version the server does not accept",
  body_too_large: "body above the hashing cap",
  store_unavailable: "counter store did not answer — request was let through",
};

/** The dot beside each result. `store_unavailable` is faint on purpose: it is
 *  the one result that looks like a refusal and is not one. */
const TONE: Record<string, string> = {
  missing: styles.dotWarn,
  invalid: styles.dotDanger,
  timestamp: styles.dotInfo,
  replay: styles.dotDanger,
  unsupported_format: styles.dotDanger,
  body_too_large: styles.dotDanger,
  store_unavailable: styles.dotFaint,
};

/**
 * Every result the counters can hold except `valid`.
 *
 * `store_unavailable` is in here, and it is the one row under this heading that
 * is not a refusal — the request was let through (fail-open, §3.4). It is listed
 * anyway, with a faint dot and a gloss saying so, because it is normally zero
 * and a number that appears from nowhere with no line to explain it is worse
 * than a row whose gloss corrects the heading above it.
 */
const KNOWN_RESULTS = Object.keys(GLOSS);

/**
 * The rows of the breakdown: every non-zero result in the answer, whether or
 * not this build has heard of it.
 *
 * The reader on the server deliberately keeps labels it does not know, so that
 * the share of signed traffic cannot be inflated by a newer writer's verdict.
 * Dropping those labels here would undo exactly that: they would be inside the
 * "would be refused" figure and inside nothing else, and the table would
 * quietly sum to less than the number above it.
 */
function breakdownRows(byResult: Record<string, number>) {
  return Object.entries(byResult)
    .filter(([result, count]) => result !== "valid" && count > 0)
    .map(([result, count]) => ({ result, count }))
    .sort((a, b) => b.count - a.count);
}

/** Two decimals for the table, and a floor rather than a rounded-down zero:
 *  118 replays out of twelve million is not "0.00 %". */
function formatSmallShare(count: number, total: number): string {
  const pct = total > 0 ? (count / total) * 100 : 0;
  if (pct > 0 && pct < 0.01) return "< 0.01 %";
  return `${pct.toFixed(2)} %`;
}

export function RequestSigningStats({
  stats,
  failed,
  mode,
  clockSkewSec,
}: Props) {
  // Still in flight: nothing is claimed, in either direction.
  if (!failed && !stats) return null;

  // The store did not answer. A breakage, and it says so — the setting below
  // is unaffected and still saves.
  if (failed || !stats || !stats.available) {
    return (
      <div className={`${styles.rollout} ${styles.empty}`}>
        <span className={`${styles.emptyIcon} ${styles.emptyIconWarn}`}>
          <Ban size={16} />
        </span>
        <div>
          <div className={styles.emptyTitle}>No data</div>
          <div className={styles.emptyText}>
            The counter store did not answer. The setting below still saves —
            only the numbers are missing.
          </div>
        </div>
      </div>
    );
  }

  const rollout = signingRollout(stats);
  if (!rollout) {
    // Nothing counted. The normal life of a project before its first log run,
    // which is a different story from the one above and gets its own words.
    return (
      <div className={`${styles.rollout} ${styles.empty}`}>
        <span className={styles.emptyIcon}>
          <LineChart size={16} />
        </span>
        <div>
          <div className={styles.emptyTitle}>Nothing measured yet</div>
          <div className={styles.emptyText}>
            {mode === "log" || mode === "enforce" ? (
              <>
                Counters are written from the first request that arrives in{" "}
                <code>{mode}</code>. Nothing has been counted in the last{" "}
                {stats.window_days} days yet.
              </>
            ) : (
              <>
                Counters are written in <code>log</code> and{" "}
                <code>enforce</code> only. Switch to <code>log</code> to find
                out how many requests already arrive signed — that is the number{" "}
                <code>enforce</code> is decided by.
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const rows = breakdownRows(stats.by_result);
  // Only the results this build knows about can be reported as absent: a label
  // it has never heard of is not missing from the period, it is missing from
  // the vocabulary.
  const silent = KNOWN_RESULTS.filter((r) => !(stats.by_result[r] > 0));

  // The last segment takes what the first two left rather than its own share of
  // the total. The three counts come from summing seven daily hashes, and a
  // read torn across a write can hand back a valid + fail-open pair larger than
  // the total it was given: computed independently, the bar would then run past
  // 100 % and `overflow: hidden` would silently eat the hatched segment — the
  // one the three-segment scale exists for.
  const validPct = Math.min(100, (rollout.valid / rollout.total) * 100);
  const refusedPct = Math.min(100 - validPct, (rollout.refused / rollout.total) * 100);
  const uncheckedPct = Math.max(0, 100 - validPct - refusedPct);

  return (
    <div className={styles.rollout}>
      <div className={styles.head}>
        <span className={styles.kicker}>
          Signed requests · last {stats.window_days} days
        </span>
        <span className={styles.total}>
          {formatCount(rollout.total)} requests counted
        </span>
      </div>
      <div className={styles.figure}>
        <span className={styles.pct}>
          {(rollout.signedShare * 100).toFixed(1)}
          <span className={styles.unitSign}>%</span>
        </span>
        <span className={styles.caption}>
          signed — <b className={styles.captionBad}>
            {formatShare(rollout.refusedShare)}
          </b>{" "}
          ({formatCount(rollout.refused)}) would be refused in{" "}
          <code>enforce</code>
        </span>
      </div>
      <div
        className={styles.bar}
        role="img"
        aria-label={`${formatShare(rollout.signedShare)} valid, ${formatShare(
          rollout.refusedShare,
        )} would be refused`}
      >
        <span className={styles.segValid} style={{ width: `${validPct}%` }} />
        <span
          className={styles.segRefused}
          style={{ width: `${refusedPct}%` }}
        />
        <span
          className={styles.segUnchecked}
          style={{ width: `${uncheckedPct}%` }}
        />
      </div>
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={`${styles.swatch} ${styles.segValid}`} />
          valid <b>{formatCount(rollout.valid)}</b>
        </span>
        <span className={styles.legendItem}>
          <span className={`${styles.swatch} ${styles.segRefused}`} />
          would be refused <b>{formatCount(rollout.refused)}</b>
        </span>
        <span className={styles.legendItem}>
          <span className={`${styles.swatch} ${styles.segUnchecked}`} />
          passed unchecked <b>{formatCount(rollout.unchecked)}</b>
        </span>
      </div>

      {rows.length > 0 && (
        <div className={styles.breakdown}>
          <table>
            <thead>
              <tr>
                <th colSpan={2}>Refusals by result</th>
                <th className={styles.num}>count</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.result}>
                  <td className={styles.res}>
                    <span
                      className={`${styles.dot} ${TONE[row.result] ?? styles.dotFaint}`}
                    />
                    <code>{row.result}</code>
                  </td>
                  <td className={styles.gloss}>
                    {row.result === "timestamp"
                      ? `client clock outside the ±${clockSkewSec} s window`
                      : (GLOSS[row.result] ??
                        // A verdict newer than this build. Counted and named,
                        // because the alternative is a number in the total that
                        // no row accounts for.
                        "a result this admin build does not know yet")}
                  </td>
                  <td className={styles.num}>
                    {formatCount(row.count)}
                    <span className={styles.share}>
                      {formatSmallShare(row.count, rollout.total)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {silent.length > 0 && (
            // Naming them costs one grey line and answers "what is missing from
            // this list" — eight rows of which six are zeroes answers nothing.
            <div className={styles.zeroNote}>
              No hits in this period:{" "}
              {silent.map((result, i) => (
                <span key={result}>
                  {i > 0 && ", "}
                  <code>{result}</code>
                </span>
              ))}
              .
            </div>
          )}
        </div>
      )}
    </div>
  );
}
